import { useEffect, useMemo, useRef, useState } from 'react';

import { fetchTpnStockProductDetails } from './kioskApi';

export function usePalletProductLookup({ palletCode, storedProductName }) {
  const [lookupByPalletCode, setLookupByPalletCode] = useState({});
  const [retryToken, setRetryToken] = useState(0);
  const isMountedRef = useRef(true);
  const latestLookupKeyRef = useRef('');
  const lookupKey = palletCode.toLowerCase();
  const lookup = lookupKey ? (lookupByPalletCode[lookupKey] ?? null) : null;

  latestLookupKeyRef.current = lookupKey;

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!palletCode || palletCode === 'Pending' || storedProductName) {
      return;
    }

    if (lookup?.status === 'pending' || lookup?.status === 'success') {
      return;
    }

    setLookupByPalletCode((current) => ({
      ...current,
      [lookupKey]: {
        status: 'pending',
        products: current[lookupKey]?.products ?? [],
        calculatedGoodsWeightKg: current[lookupKey]?.calculatedGoodsWeightKg ?? null,
      },
    }));

    let active = true;
    const controller = new AbortController();

    fetchTpnStockProductDetails(palletCode, { signal: controller.signal })
      .then((productDetails) => {
        if (!active) {
          return;
        }

        const products = [...new Set(
          productDetails.map((product) => product.productName).filter(Boolean),
        )];
        const calculatedGoodsWeights = productDetails
          .map((product) => product.calculatedGoodsWeightKg)
          .filter((weight) => Number.isFinite(weight));

        setLookupByPalletCode((current) => ({
          ...current,
          [lookupKey]: {
            status: 'success',
            products,
            calculatedGoodsWeightKg: calculatedGoodsWeights.length > 0
              ? calculatedGoodsWeights.reduce((total, weight) => total + weight, 0)
              : null,
          },
        }));
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          if (!isMountedRef.current) {
            return;
          }

          let resetLookup = false;
          setLookupByPalletCode((current) => {
            const existingLookup = current[lookupKey];
            if (!existingLookup || existingLookup.status !== 'pending') {
              return current;
            }

            resetLookup = true;
            return {
              ...current,
              [lookupKey]: {
                status: 'idle',
                products: existingLookup.products ?? [],
                calculatedGoodsWeightKg: existingLookup.calculatedGoodsWeightKg ?? null,
              },
            };
          });

          if (resetLookup && latestLookupKeyRef.current === lookupKey) {
            setRetryToken((value) => value + 1);
          }
          return;
        }

        if (!active) {
          return;
        }

        setLookupByPalletCode((current) => ({
          ...current,
          [lookupKey]: {
            status: 'error',
            products: current[lookupKey]?.products ?? [],
            calculatedGoodsWeightKg: current[lookupKey]?.calculatedGoodsWeightKg ?? null,
          },
        }));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [lookupKey, palletCode, retryToken, storedProductName]);

  return useMemo(() => {
    if (!palletCode || palletCode === 'Pending') {
      return '';
    }
    if (storedProductName) {
      return storedProductName;
    }
    if (!lookup || lookup.status === 'idle') {
      return '';
    }
    if (lookup.status === 'pending') {
      return 'กำลังโหลดสินค้า...';
    }
    if (lookup.status === 'error') {
      return 'ไม่สามารถโหลดสินค้าได้';
    }
    if (!lookup.products?.length) {
      return 'ไม่พบสินค้า';
    }

    return lookup.products.length === 1
      ? `${lookup.products[0]}`
      : `${lookup.products.join(', ')}`;
  }, [lookup, palletCode, storedProductName]);
}
